import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Syncs the sold-out attribute on the closest product-card element so that
   * the existing CSS selectors (product-card[sold-out] product-price .price) apply
   * automatically — no inline styles needed.
   *
   * Also hides the compare-at price span when sold out. The Liquid already suppresses
   * it on the initial render, but after a client-side variant swap the server response
   * may still carry a compare-at element if it was cached; this guards against that.
   *
   * @param {Element} priceContainer - The [ref="priceContainer"] element just inserted into the DOM.
   */
  #syncSoldOutState(priceContainer) {
    const isSoldOut = priceContainer.dataset.soldOut === 'true';

    // Drive the existing CSS rules:
    //   product-card[sold-out] product-price .price { color: #888888; font-weight: 400 }
    const productCard = this.closest('product-card');
    if (productCard) {
      if (isSoldOut) {
        productCard.setAttribute('sold-out', '');
      } else {
        productCard.removeAttribute('sold-out');
      }
    }

    // Belt-and-suspenders: hide the compare-at element directly so no discount
    // is ever visible on a sold-out variant, regardless of server response caching.
    const compareAtPrice = priceContainer.querySelector('.compare-at-price');
    if (compareAtPrice instanceof HTMLElement) {
      compareAtPrice.hidden = isSoldOut;
    }
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) {
      currentPrice.replaceWith(newPrice);

      // Sync sold-out state after the new container is in the DOM
      this.#syncSoldOutState(/** @type {Element} */ (newPrice));
    }

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
